/**
 * RepConnect - CRM Export Functionality
 * 
 * Handles exporting provider profiles and enhancements to various CRM formats
 * while respecting privacy controls and data source hierarchy.
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 */

import { CRM_EXPORT_FORMATS, PRIVACY_LEVELS } from './profileEnhancements.js';
import ProfileEnhancementStorage from './profileEnhancementStorage.js';
import { logger } from './logger';

export const CRMExport = {

  /**
   * Export provider profile to specified CRM format
   * @param {Object} provider - Provider data
   * @param {string} userId - User requesting export
   * @param {string} format - CRM format ('salesforce', 'hubspot', 'pipedrive', 'generic')
   * @returns {Object} Formatted export data
   */
  exportProvider(provider, userId, format = 'generic') {
    try {
      // Get enhanced profile data
      const enhancements = ProfileEnhancementStorage.getProviderEnhancements(
        provider.id, 
        userId, 
        'sales' // Assuming sales rep export
      );

      // Build base provider data
      const exportData = {
        // Export metadata
        exportInfo: {
          providerId: provider.id,
          exportedAt: new Date().toISOString(),
          exportedBy: userId,
          format: format,
          version: '1.0'
        },

        // Core provider information (always included)
        basicInfo: {
          name: provider.name,
          specialty: provider.specialty,
          npi: provider.npi,
          address: provider.address,
          city: provider.city,
          state: provider.state,
          zipCode: provider.zipCode,
          practice: provider.hospital
        },

        // Enhanced data (privacy filtered)
        enhancedData: {},

        // Meeting insights (derived data)
        meetingInsights: this.getMeetingInsights(provider.id, userId),

        // Data source information
        dataSources: this.getDataSourceSummary(provider, enhancements)
      };

      // Add enhanced fields based on privacy settings
      Object.keys(enhancements).forEach(fieldName => {
        const enhancement = enhancements[fieldName].current;
        
        // Only include public and professional data in exports
        if (enhancement.privacyLevel === PRIVACY_LEVELS.PUBLIC || 
            enhancement.privacyLevel === PRIVACY_LEVELS.PROFESSIONAL) {
          
          exportData.enhancedData[fieldName] = {
            value: enhancement.value,
            source: enhancement.dataSource,
            lastUpdated: enhancement.updatedAt,
            verified: enhancement.verified || false,
            confidence: enhancement.derivedFrom?.confidence || 1.0
          };
        }
      });

      // Format for specific CRM
      if (format !== 'generic') {
        return this.formatForCRM(exportData, format);
      }

      return exportData;
    } catch (error) {
      logger.error('Error exporting provider:', error);
      throw new Error(`Failed to export provider: ${error.message}`);
    }
  },

  /**
   * Format export data for specific CRM system
   * @param {Object} exportData - Generic export data
   * @param {string} format - CRM format
   * @returns {Object} CRM-specific formatted data
   */
  formatForCRM(exportData, format) {
    const crmFormat = CRM_EXPORT_FORMATS[format.toUpperCase()];
    if (!crmFormat) {
      throw new Error(`Unsupported CRM format: ${format}`);
    }

    const formatted = {
      format: crmFormat.name,
      exportedAt: exportData.exportInfo.exportedAt,
      contact: {},
      customFields: {},
      notes: [],
      tags: []
    };

    // Map basic fields
    const basicInfo = exportData.basicInfo;
    formatted.contact = {
      firstName: this.extractFirstName(basicInfo.name),
      lastName: this.extractLastName(basicInfo.name),
      company: basicInfo.practice,
      title: basicInfo.specialty,
      address: basicInfo.address,
      city: basicInfo.city,
      state: basicInfo.state,
      postalCode: basicInfo.zipCode
    };

    // Map enhanced fields using CRM field mapping
    Object.entries(exportData.enhancedData).forEach(([fieldName, data]) => {
      const crmFieldName = crmFormat.fields[fieldName];
      if (crmFieldName) {
        if (crmFieldName.startsWith('custom_') || crmFieldName.includes('__c')) {
          formatted.customFields[crmFieldName] = data.value;
        } else {
          formatted.contact[crmFieldName] = data.value;
        }
      }
    });

    // Add data source information as tags
    formatted.tags = Object.keys(exportData.dataSources).map(source => 
      `data-source-${source.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
    );

    // Add meeting insights as notes
    if (exportData.meetingInsights) {
      formatted.notes.push({
        content: this.formatMeetingInsightsAsNote(exportData.meetingInsights),
        type: 'meeting-insights',
        createdAt: exportData.exportInfo.exportedAt
      });
    }

    return formatted;
  },

  /**
   * Extract first name from full name
   * @param {string} fullName - Full provider name
   * @returns {string} First name
   */
  extractFirstName(fullName) {
    const nameParts = fullName.replace(/^Dr\.?\s*/, '').split(' ');
    return nameParts.length > 1 ? nameParts[0] : '';
  },

  /**
   * Extract last name from full name
   * @param {string} fullName - Full provider name
   * @returns {string} Last name
   */
  extractLastName(fullName) {
    const nameParts = fullName.replace(/^Dr\.?\s*/, '').split(' ');
    return nameParts.length > 1 ? nameParts.slice(1).join(' ') : fullName;
  },

  /**
   * Get meeting insights for export
   * @param {string} providerId - Provider ID
   * @param {string} userId - User ID
   * @returns {Object} Meeting insights summary
   */
  getMeetingInsights(providerId, userId) {
    try {
      // Import MeetingStorage dynamically to avoid circular dependency
      const { MeetingStorage } = require('./meetingStorage.js');
      return MeetingStorage.getDerivedPreferencesForProvider(providerId, userId);
    } catch (error) {
      logger.warn('Could not load meeting insights:', error);
      return null;
    }
  },

  /**
   * Get data source summary
   * @param {Object} provider - Provider data
   * @param {Object} enhancements - Enhancement data
   * @returns {Object} Data source summary
   */
  getDataSourceSummary(provider, enhancements) {
    const sources = {};

    // Add base provider source
    if (provider.source) {
      sources[provider.source] = {
        type: 'base_provider',
        fields: ['name', 'specialty', 'address', 'npi'],
        reliability: 'high'
      };
    }

    // Add enhancement sources
    Object.values(enhancements).forEach(enhancement => {
      const current = enhancement.current;
      if (!sources[current.dataSource]) {
        sources[current.dataSource] = {
          type: 'enhancement',
          fields: [],
          reliability: this.getSourceReliability(current.dataSource)
        };
      }
      sources[current.dataSource].fields.push(current.fieldName);
    });

    return sources;
  },

  /**
   * Get reliability score for data source
   * @param {string} dataSource - Data source type
   * @returns {string} Reliability level
   */
  getSourceReliability(dataSource) {
    const reliabilityMap = {
      'npi_registry': 'high',
      'provider_verified': 'high',
      'user_contributed': 'medium',
      'meeting_derived': 'medium'
    };
    return reliabilityMap[dataSource] || 'low';
  },

  /**
   * Format meeting insights as CRM note
   * @param {Object} insights - Meeting insights data
   * @returns {string} Formatted note content
   */
  formatMeetingInsightsAsNote(insights) {
    if (!insights || insights.totalMeetings === 0) {
      return 'No meeting insights available.';
    }

    let note = `Meeting Insights (${insights.totalMeetings} meetings):\n\n`;

    // Restaurant preferences
    const restaurants = Object.entries(insights.preferences.restaurants);
    if (restaurants.length > 0) {
      note += 'Preferred Restaurants:\n';
      restaurants
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .forEach(([restaurant, count]) => {
          note += `• ${restaurant} (${count} positive experiences)\n`;
        });
      note += '\n';
    }

    // Time preferences
    const timeSlots = Object.entries(insights.preferences.timeSlots);
    if (timeSlots.length > 0) {
      note += 'Meeting Time Preferences:\n';
      timeSlots
        .sort(([,a], [,b]) => b - a)
        .forEach(([slot, count]) => {
          const slotName = slot === 'early' ? 'Early lunch (11:00-12:00)' :
                          slot === 'standard' ? 'Standard lunch (12:00-1:00)' :
                          'Late lunch (1:00-2:00)';
          note += `• ${slotName}: ${count} meetings\n`;
        });
      note += '\n';
    }

    note += `Last analyzed: ${new Date(insights.lastAnalyzed).toLocaleDateString()}`;
    return note;
  },

  /**
   * Export multiple providers to CSV format
   * @param {Array} providers - Array of provider objects
   * @param {string} userId - User requesting export
   * @returns {string} CSV content
   */
  exportToCSV(providers, userId) {
    const headers = [
      'Name',
      'Specialty',
      'NPI',
      'Practice',
      'Address',
      'City', 
      'State',
      'ZIP',
      'Phone',
      'Email',
      'Preferred Meeting Time',
      'Dietary Restrictions',
      'Favorite Restaurants',
      'Total Meetings',
      'Data Sources'
    ];

    let csvContent = headers.join(',') + '\n';

    providers.forEach(provider => {
      const exportData = this.exportProvider(provider, userId, 'generic');
      
      const row = [
        this.escapeCSV(exportData.basicInfo.name),
        this.escapeCSV(exportData.basicInfo.specialty),
        this.escapeCSV(exportData.basicInfo.npi),
        this.escapeCSV(exportData.basicInfo.practice),
        this.escapeCSV(exportData.basicInfo.address),
        this.escapeCSV(exportData.basicInfo.city),
        this.escapeCSV(exportData.basicInfo.state),
        this.escapeCSV(exportData.basicInfo.zipCode),
        this.escapeCSV(exportData.enhancedData.officePhone?.value || ''),
        this.escapeCSV(exportData.enhancedData.officeEmail?.value || ''),
        this.escapeCSV(exportData.enhancedData.preferredMeetingTime?.value || ''),
        this.escapeCSV(this.arrayToString(exportData.enhancedData.dietaryRestrictions?.value)),
        this.escapeCSV(this.arrayToString(exportData.enhancedData.preferredRestaurants?.value)),
        exportData.meetingInsights?.totalMeetings || 0,
        this.escapeCSV(Object.keys(exportData.dataSources).join('; '))
      ];

      csvContent += row.join(',') + '\n';
    });

    return csvContent;
  },

  /**
   * Escape CSV field content
   * @param {string} field - Field content
   * @returns {string} Escaped content
   */
  escapeCSV(field) {
    if (!field) return '';
    const str = String(field);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  },

  /**
   * Convert array to string for CSV
   * @param {Array} arr - Array to convert
   * @returns {string} String representation
   */
  arrayToString(arr) {
    if (!Array.isArray(arr)) return '';
    return arr.join('; ');
  },

  /**
   * Download export data as file
   * @param {Object|string} data - Export data
   * @param {string} filename - File name
   * @param {string} contentType - MIME type
   */
  downloadFile(data, filename, contentType = 'application/json') {
    const content = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Generate export filename
   * @param {Object} provider - Provider data
   * @param {string} format - Export format
   * @returns {string} Filename
   */
  generateFilename(provider, format) {
    const cleanName = provider.name.replace(/[^a-zA-Z0-9]/g, '_');
    const timestamp = new Date().toISOString().split('T')[0];
    
    const extension = format === 'csv' ? 'csv' : 'json';
    return `${cleanName}_${format}_export_${timestamp}.${extension}`;
  }
};

export default CRMExport;